'use client';

import React, { useContext, useEffect, useState } from 'react';
import { useParams, useRouter } from 'next/navigation';
import { differenceInMilliseconds } from 'date-fns';
import { useI18n } from '@locales/client';

import { ActiveBonusPopoverDynamic } from '@/features/active-bonus-popover/dynamic';
import { shouldShowActiveBonusPopover } from '@/features/active-bonus-popover/model/should-show-active-bonus-popover';
import { NotifyManagement } from '@/features/notify-management';
import {
  useFullscreenHandler,
  useNeedFullscreenButton,
} from '@/shared/providers/fullscreen-provider/store';
import { AppInstallTrigger } from '@/flat-pages/installation-guide/ui/app-install-trigger';
import { BonusTimer } from '@/features/gold-card/components/bonus-timer';
import { Button } from '@/shared/ui/form/button';
import { Condition } from '@/shared/ui/condition';
import { AuthModals } from './components/auth-modals';
import { HeaderGameBonusWarning } from './components/game-bonus-warning';
import {
  AuthorizedActions,
  AuthorizedRow,
  AvatarIcon,
  BalanceColumn,
  BodyTextMediumBModern,
  BodyTextMediumDModern,
  GameBonusWarningSlot,
  GuestActions,
  LanguageSlot,
  FullScreenImage,
  FullscreenButton,
  HeaderButton,
  ProfileButtonSlot,
  WalletIcon,
} from './styles';
import { useFormatAmount } from '@/shared/hooks/useFormatAmount';

import { useNotifications } from '@/shared/providers/notifications-provider/notifications-context';
import {
  useIsAuth,
  useIsUserLoading,
} from '@/shared/providers/user-provider/use-user';
import { SelectLanguageDynamic as SelectLanguage } from '@/shared/ui/selects/select-language';
import { AuthActionButtons } from '@/shared/ui/auth-buttons';
import { WalletContext } from '@/shared/providers/wallet-context';
import styles from './server-header.module.css';

const WALLET_ICON_SRC = '/images/icons/wallet-balance.svg';
const WALLET_ICON_SIZE = 28;
const AVATAR_ICON_SRC = '/images/icons/avatar-default.svg';
const AVATAR_ICON_SIZE = 20;
const PROFILE_PATH = '/profile';

const PingwinHeader = () => {
  const onFullscreenClick = useFullscreenHandler();
  const needFullscreenButton = useNeedFullscreenButton();

  const isAuth = useIsAuth();
  const isUserLoading = useIsUserLoading();
  const formatAmount = useFormatAmount();
  const { isNotifyLoading, notifications } = useNotifications();
  const walletContext = useContext(WalletContext);
  const openWalletManagement = walletContext?.openWallet;

  const [progressWager, setProgressWager] = useState(0);
  const [expiredDate, setExpireDate] = useState<{
    mm: number;
    hh: number;
    percentage: number | null;
    isThirtyPercentRemaining: boolean;
  }>({
    mm: 0,
    hh: 0,
    percentage: null,
    isThirtyPercentRemaining: false,
  });
  const router = useRouter();
  const t = useI18n();
  const params = useParams();

  const shouldRenderGameBonusWarning = Boolean(
    params?.titleGame && params?.categories
  );
  const shouldRenderActiveBonusPopover =
    shouldShowActiveBonusPopover(notifications);
  const hasBalanceInfo =
    typeof notifications?.bonus?.bonusBalance === 'number' &&
    (notifications.balance !== 0 || notifications.bonus.bonusBalance !== 0);

  useEffect(() => {
    if (
      notifications?.bonusInfo?.dateFrom &&
      notifications?.bonusInfo?.dateTo
    ) {
      const startDate = new Date(notifications.bonusInfo.dateFrom * 1000);
      const endDate = new Date(notifications.bonusInfo.dateTo * 1000);
      const totalTime = differenceInMilliseconds(endDate, startDate);
      const now = new Date();
      const remainingTime = differenceInMilliseconds(endDate, now);
      const hoursRemaining = Math.floor(remainingTime / (1000 * 60 * 60));
      const minutesRemaining = Math.floor(
        (remainingTime % (1000 * 60 * 60)) / (1000 * 60)
      );
      const percentageRemaining = (remainingTime / totalTime) * 100;
      const isThirtyPercentRemaining = percentageRemaining <= 30;

      setExpireDate({
        mm: minutesRemaining,
        hh: hoursRemaining,
        percentage: percentageRemaining,
        isThirtyPercentRemaining,
      });
    }
  }, [notifications?.bonusInfo?.dateFrom, notifications?.bonusInfo?.dateTo]);

  useEffect(() => {
    if (notifications?.bonus?.wager && notifications?.bonus?.startWager) {
      const startWager = notifications.bonus.startWager;
      const currentWager = notifications.bonus.wager;
      const currentProgress = (currentWager / startWager) * 100;
      setProgressWager(currentProgress);
    }
  }, [notifications?.bonus?.wager, notifications?.bonus?.startWager]);

  return (
    <>
      <AuthModals onClick={openWalletManagement} />
      <div className={styles.rightSlot} data-header-right-slot="true">
        <Condition match={isAuth}>
          <AuthorizedRow>
            <AuthorizedActions>
              <BonusTimer />
              <AppInstallTrigger />
              <Condition match={shouldRenderActiveBonusPopover}>
                <ActiveBonusPopoverDynamic
                  expiredDate={expiredDate}
                  progressWager={progressWager}
                />
              </Condition>

              <Condition match={shouldRenderGameBonusWarning}>
                <GameBonusWarningSlot>
                  <HeaderGameBonusWarning
                    titleGame={params?.titleGame}
                    categories={params?.categories}
                  />
                </GameBonusWarningSlot>
              </Condition>

              <HeaderButton
                data-analytics-id="deposit_button"
                $isEmptyBalance={notifications?.balance === 0}
                $isViewImg={notifications?.bonus?.isActive}
                forIcon={true}
                isLoading={isNotifyLoading}
                type="button"
                onClick={openWalletManagement}
                icon={
                  <WalletIcon
                    width={WALLET_ICON_SIZE}
                    height={WALLET_ICON_SIZE}
                    src={WALLET_ICON_SRC}
                    alt=""
                  />
                }
                variant="orangeGradient"
              >
                <BalanceColumn>
                  <Condition match={!hasBalanceInfo}>
                    <Condition match={notifications.balance === 0}>
                      {t('wallet.tabs.deposit')}
                    </Condition>
                  </Condition>

                  <Condition match={hasBalanceInfo}>
                    <BodyTextMediumBModern>
                      {formatAmount(notifications.balance || 0)}
                    </BodyTextMediumBModern>

                    <Condition match={notifications?.bonus?.bonusBalance !== 0}>
                      <BodyTextMediumDModern>
                        {`${t('wallet.bonus.percent')}: ${formatAmount(notifications?.bonus?.bonusBalance)}`}
                      </BodyTextMediumDModern>
                    </Condition>
                  </Condition>
                </BalanceColumn>
              </HeaderButton>

              {!(notifications?.bonus?.isActive && needFullscreenButton) && (
                <NotifyManagement />
              )}

              <ProfileButtonSlot>
                <Button
                  type="button"
                  variant="gray"
                  onClick={() => router.push(PROFILE_PATH)}
                  icon={
                    <AvatarIcon
                      width={AVATAR_ICON_SIZE}
                      height={AVATAR_ICON_SIZE}
                      src={AVATAR_ICON_SRC}
                      alt="User avatar"
                    />
                  }
                />
              </ProfileButtonSlot>

              {needFullscreenButton && (
                <FullscreenButton onClick={onFullscreenClick ?? undefined}>
                  <FullScreenImage />
                </FullscreenButton>
              )}
            </AuthorizedActions>
          </AuthorizedRow>
        </Condition>

        <Condition match={!isUserLoading && !isAuth}>
          <GuestActions>
            <AppInstallTrigger />
            <AuthActionButtons />
            <LanguageSlot>
              <SelectLanguage alignToLeft={false} isValueMode={true} />
            </LanguageSlot>
          </GuestActions>
        </Condition>
      </div>
    </>
  );
};

export { PingwinHeader };
